import { ButtonGroup, Divider, Text, TextButton } from '@oneaudi/unified-web-alpha-components';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { PriceSection } from '~/app/compositions/content/components/price-section';
import { SummarySection, TopSection } from '~/app/compositions/content/configuration-overview-builder';
import {
	ConfigurationImagesStyle,
	ConfigurationImageWrapperStyle,
	ConfigurationOverviewPriceWrapperStyle,
	ConfigurationOverviewStyle,
	ConfigurationSectionWrapperStyle,
	ConfigurationSummaryStyle,
} from '~/app/compositions/content/configuration-overview-style';
import { EecLabelSection } from '~/app/compositions/content/eec/eec-label-section';
import { useGalleryAltText } from '~/app/compositions/content/gallery/hooks/use-gallery-alt-text';
import { GPSRSection } from '~/app/compositions/content/gpsr';
import { SummaryCtaButton } from '~/app/compositions/content/service-footer/components/user-marketing/main-cta-button';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { i18nKeys, useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { AutoFlowGrid } from '~/app/pages/components/auto-flow-grid';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { useSelectedEquipment } from '~/app/pages/summary/hooks/use-selected-equipment';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useConfiguredCarTechnicalDataQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useImages } from '~/graphql/hooks/use-images';
import { checkEventActionUrl } from '~/graphql/utils/check-event-action-url';
import GetUrlFromImageSet from '~/graphql/utils/get-from-image-set';
import { modifyImageResolution } from '~/graphql/utils/modify-image-url';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { DATA_TEST_ID } from '~/test/data-test-id';
import { getEecLabelUrl } from '~/utils/technical-data-media-items';
import { technicalDataPresent } from '~/app/utils/technical-data-present';

export function ConfigurationOverviewLegalNl() {
	const { openDialog } = useDialogs();
	const { priceSummary } = useCatalog();
	const { isLoading, carIdentifier } = useConfigurator();
	const vatConfig = useVATConfig();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const { config } = useMarketConfig();
	const { i18n } = useI18n();
	const disclaimerText = useAtomValue(state.financingDisclaimerText);
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);
	const { consumptionAndEmissionSummary } = useMarketUiConfig();

	const { imagesByProfileId, mediaByProfileId } = useImages();
	const {
		configuration: { featuresAndPackages },
	} = useSelectedEquipment();
	const [color, seats] = ['Exterior_Colors', ':Interior_Tiles'].map((id) =>
		featuresAndPackages.find((feature) => feature.id.includes(id)),
	);
	let priceRemark: string | null = null;

	if (config.useStaticPriceDateRemark) {
		priceRemark = config.useSummaryAlternativePriceDateRemark
			? i18n('ngc.summary.alternativeRemark')
			: i18n('ngc.summary.remark');
	} else if (priceSummary?.priceDate?.text) {
		priceRemark = priceSummary?.priceDate?.text;
	}

	const priceOrder = ['total', 'rotrRate', 'divider'];
	const prices = priceSummary?.parts || [];
	const totalPrice = prices.find((price) => price.type === 'rotr');

	const { loading, data: technicalData } = useConfiguredCarTechnicalDataQuery(carIdentifier);

	const eecUrl = useMemo(() => {
		const url = getEecLabelUrl(technicalData?.configuredCar?.technicalDataV2?.mediaItems);
		return url || undefined;
	}, [technicalData?.configuredCar?.technicalDataV2?.mediaItems, technicalData]);

	const impressionContainerRef = useCallback(
		(node: HTMLElement) => {
			if (node !== null && !isLoading) {
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview) - impression',
					componentName: 'configuration overview',
				});
			}
		},
		[isLoading],
	);

	const impressionCTARef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading) {
				const buttonNodes = Array.from(node.childNodes[0].childNodes || []);
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview buttons) - impression',
					componentName: 'configuration overview',
					relatedElements: buttonNodes.map((buttonNode, index) => ({
						elementName: 'cta',
						elementValue: `${index + 1}-${(buttonNode as HTMLElement).innerText}`,
					})),
				});
			}
		},
		[isLoading],
	);

	const trackClickEvent = useCallback((eventName: string, label: string, url?: string) => {
		const buttonsContainerRef = document.querySelector('.buttons-group-cta');
		const buttonNodes = buttonsContainerRef?.getElementsByTagName('button');
		const nodeIndex = Array.from(buttonNodes || [])?.findIndex((buttonNode) => buttonNode.innerText === label);

		if (buttonNodes && nodeIndex !== -1) {
			appTrackingService.summaryClickEvent({
				eventAction: checkEventActionUrl(url || ''),
				eventName: `configurator (configuration overview) - click on ${eventName}`,
				componentName: 'configurator-configuration-overview',
				label,
				targetURL: url,
				elementName: 'button',
				relatedElements: [
					{
						elementName: 'cta',
						elementValue: `${nodeIndex + 1}-${buttonNodes[nodeIndex].id}`,
					},
				],
			});
		}
	}, []);

	return (
		<ConfigurationOverviewStyle
			ref={impressionContainerRef}
			data-testid={DATA_TEST_ID.SUMMARY_CONFIGURATION_OVERVIEW_CONTAINER}>
			<TopSection techDataPresent={technicalDataPresent(technicalData?.configuredCar?.technicalDataV2)} />
			<ConfigurationSummaryStyle>
				<ConfigurationSectionWrapperStyle>
					<SummarySection />
					<Divider />
					{config.showPrices && (
						<div className="summary__price-list">
							{priceOrder.map((price) => {
								const labelId = `summary-price-label-${price}`;
								const priceId = `summary-price-${price}`;
								return price === 'divider' ? (
									<Divider key={price} />
								) : (
									<ConfigurationOverviewPriceWrapperStyle
										key={price}
										tabIndex={0}
										aria-labelledby={`${labelId} ${priceId}`}>
										<div className="summary__price-headline">
											<Text id={labelId} variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{i18n(`ngc.priceDetails.${price}` as keyof typeof i18nKeys)}
											</Text>
										</div>
										<div className="summary__price-value">
											<Text
												id={priceId}
												className="alignRight"
												variant={{ type: 'copy', order: '1', style: 'normal' }}>
												{formatCurrency(
													prices.find((part) => part.type === price)?.price,
													vatConfig.priceParts[price],
												)}
											</Text>
										</div>
									</ConfigurationOverviewPriceWrapperStyle>
								);
							})}

							{config.showPrices && (
								<PriceSection
									totalPrice={formatCurrency(totalPrice?.price) ?? ''}
									monthlyRate={formatMonthlyRate(priceSummary?.monthlyRate)}
									remark={priceRemark}
									disclaimer={disclaimerText}
									showSpecialOffers={config.specialOfferUrl.length > 0}
								/>
							)}
						</div>
					)}
					<div ref={impressionCTARef}>
						<ButtonGroup className="buttons-group-cta" orientation="vertical">
							{config.showPrices && (
								<TextButton
									id="price details"
									size={'md'}
									variant="secondary"
									onClick={() => {
										openDialog('priceDetails');
										trackClickEvent(
											'open price details layer',
											i18n('ngc.actions.showPriceDetails'),
										);
									}}
									isFullWidth
									aria-label={i18n('ngc.actions.showPriceDetails')}
									data-testid={DATA_TEST_ID.SUMMARY_PRICE_DETAIL_BUTTON}>
									{i18n('ngc.actions.showPriceDetails')}
								</TextButton>
							)}
							<SummaryCtaButton
								trackClickEvent={trackClickEvent}
								variant="secondary"
								size="md"
								isSecondCta="false"
								isFullWidth
							/>
							{StockCarEnabled() && (
								<TextButton
									id="Car Matching"
									className="main-cta-button"
									isFullWidth
									variant="secondary"
									onClick={() => {
										showStockCarDialog();
										trackClickEvent('stock car matching', i18n('ngc.stockcar.trigger'));
									}}
									size={'md'}
									aria-label={i18n('ngc.stockcar.trigger')}
									data-testid={DATA_TEST_ID.STOCK_CAR_BUTTON}>
									{i18n('ngc.stockcar.trigger')}
								</TextButton>
							)}
						</ButtonGroup>
					</div>
				</ConfigurationSectionWrapperStyle>
				<ConfigurationImageWrapperStyle>
					<ConfigurationImagesStyle>
						<AutoFlowGrid
							direction="bigImageFirst"
							sources={[
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4],
										'960',
									),
									alt: useGalleryAltText(mediaByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4]),
								},
								{
									src:
										GetUrlFromImageSet(
											color?.families?.[0]?.items?.[0]?.imageSets,
											'outer_tile_button',
										) ||
										GetUrlFromImageSet(
											color?.families?.[0]?.items?.[0]?.imageSets,
											'outer_tile_hd_square',
										) ||
										'//about:blank',
									alt: color?.families?.[0]?.items?.[0].name || i18n('ngc.a11y.tiles.notavailable'),
								},
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM],
										'480',
									),
									alt: useGalleryAltText(mediaByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM]),
								},
							]}
						/>
						<AutoFlowGrid
							direction="smallImagesFirst"
							sources={[
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS],
										'480',
									),
									alt: useGalleryAltText(mediaByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS]),
								},
								{
									src:
										GetUrlFromImageSet(
											seats?.families?.[0]?.items?.[0]?.imageSets,
											'interior_tile_button',
										) || '//about:blank',
									alt: seats?.families?.[0]?.items?.[0].name || i18n('ngc.a11y.tiles.notavailable'),
								},
								{
									src: modifyImageResolution(
										imagesByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD],
										'960',
									),
									alt: useGalleryAltText(
										mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD],
									),
								},
							]}
						/>
						{consumptionAndEmissionSummary}
					</ConfigurationImagesStyle>
				</ConfigurationImageWrapperStyle>
			</ConfigurationSummaryStyle>
			{!loading && config.showEECLabel && config.wltpInfo && eecUrl && <EecLabelSection eecURL={eecUrl} />}
			<GPSRSection />
		</ConfigurationOverviewStyle>
	);
}
